import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Check, CheckSquare, FileText, Loader2, MessageSquareText, Search, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { searchHitKey, SEARCH_TYPE_LABELS, SEARCH_TYPE_PLURALS, SEARCH_DEFAULT_TYPES, SEARCH_TYPES } from '@cerebro-diem/core';
import type { SearchType } from '@cerebro-diem/core';
import { CONTAINER_ICONS } from '../features/containers/labels';
import { errorMessage } from '../services/errors';
import { searchItems } from '../services/search';

/** How long typing pauses before a search runs. */
const DEBOUNCE_MS = 300;

const TYPE_ICONS: Record<SearchType, LucideIcon> = {
  task: CheckSquare,
  note: FileText,
  project: CONTAINER_ICONS.project,
  area: CONTAINER_ICONS.area,
  resource: CONTAINER_ICONS.resource,
  person: User,
  capture: MessageSquareText,
};

// Each type's color when its filter is on, and on its result badge. Theme tokens only.
const TYPE_COLORS: Record<SearchType, string> = {
  task: 'bg-primary text-on-primary',
  note: 'bg-secondary text-on-primary',
  project: 'bg-tertiary text-on-primary',
  area: 'bg-primary-container text-on-surface',
  resource: 'bg-secondary-container text-on-surface',
  person: 'bg-tertiary/20 text-on-surface',
  capture: 'bg-on-surface-variant text-surface',
};

/** `value`, once it has stopped changing for `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<SearchType>>(() => new Set(SEARCH_DEFAULT_TYPES));
  const term = useDebounced(query.trim(), DEBOUNCE_MS);
  const types = SEARCH_TYPES.filter((type) => selected.has(type));

  const search = useQuery({
    queryKey: ['search', term, types],
    queryFn: () => searchItems(term, types),
    enabled: term.length > 0 && types.length > 0,
    placeholderData: keepPreviousData,
  });

  const toggle = (type: SearchType) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });

  const active = term.length > 0 && types.length > 0;
  const results = active ? search.data : undefined;

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-on-surface">Search</h1>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-on-surface-variant" aria-hidden="true" />
        <input
          type="search"
          aria-label="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tasks, notes, projects, areas, resources, people and captures"
          autoFocus
          className="w-full pl-12 pr-12 py-3 text-lg rounded-xl border border-outline/40 bg-surface text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {active && search.isFetching && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-primary" aria-label="Searching" />
        )}
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Types to search">
        {SEARCH_TYPES.map((type) => {
          const on = selected.has(type);
          const Icon = on ? Check : TYPE_ICONS[type];
          return (
            <button
              key={type}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(type)}
              className={`inline-flex items-center gap-1.5 min-h-11 pointer-fine:min-h-0 px-3 py-1 rounded-full text-sm font-medium border transition ${
                on ? `${TYPE_COLORS[type]} border-transparent` : 'bg-surface border-outline/40 text-on-surface-variant hover:bg-surface-variant/40'
              }`}
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
              {SEARCH_TYPE_PLURALS[type]}
            </button>
          );
        })}
      </div>

      {types.length === 0 && <p className="text-sm text-on-surface-variant">Turn on at least one type to search.</p>}

      {active && search.isError && (
        <p role="alert" className="text-sm text-error">
          Search failed: {errorMessage(search.error)}
        </p>
      )}

      {results?.meaningUnavailable && (
        // Meaning matches are missing, not the results. The text matches below
        // are real and are shown; this line is why the list may be shorter than
        // usual, so a short list is never mistaken for nothing to find.
        <p role="status" className="text-sm text-tertiary">
          Search by meaning is unavailable right now; these are text matches only.
        </p>
      )}

      {results && (
        <>
          {results.hits.length === 0 ? (
            <p className="text-center py-12 text-on-surface-variant">No matches for “{term}”.</p>
          ) : (
            <ul className="space-y-2" aria-label="Results">
              {results.hits.map((hit) => {
                const Icon = TYPE_ICONS[hit.type];
                return (
                  <li key={searchHitKey(hit)}>
                    <Link
                      to={hit.to}
                      className="flex items-start gap-3 p-3 rounded-xl border border-outline/20 bg-surface hover:bg-surface-variant/30"
                    >
                      <Icon className="w-5 h-5 mt-0.5 shrink-0 text-on-surface-variant" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[hit.type]}`}>
                            {SEARCH_TYPE_LABELS[hit.type]}
                          </span>
                          <span className="font-medium text-on-surface truncate">{hit.title}</span>
                          {hit.marks.map((mark) => (
                            <span key={mark} className="text-xs font-medium text-tertiary">
                              {mark}
                            </span>
                          ))}
                        </div>
                        {hit.snippet && <p className="text-sm text-on-surface-variant truncate mt-0.5">{hit.snippet}</p>}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {results.fullTypes.length > 0 && (
            <p className="text-xs text-on-surface-variant">
              More {results.fullTypes.map((type) => SEARCH_TYPE_PLURALS[type].toLowerCase()).join(', ')} match than are shown; add a
              word to narrow the search.
            </p>
          )}
        </>
      )}
    </div>
  );
}
